import React, { useEffect, useState } from 'react';
import { Phone, MessageSquare, Navigation } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toWhatsAppNumber } from '@/utils/contactInfo';
import { buildWazeLink } from '@/utils/mapLinks';
import { createPortal } from 'react-dom';
import { trackContactClick, trackListingMessageClick } from '@/utils/contactTracking';

interface Props {
  phone?: string;
  userId?: string;
  /** Adresa afișată pe harta din pagina anunțului (aceeași sursă). */
  address?: string | null;
  onMessageClick: () => void;
  sellerType?: string | null;
  /** Called instead of onMessageClick when direct contact is locked. */
  onRequestClick?: () => void;
  listingId?: string;
}

export const MobileContactBar: React.FC<Props> = ({ phone, userId, address, onMessageClick, sellerType, onRequestClick, listingId }) => {
  const [fixPhone, setFixPhone] = useState<string | null>(null);
  const [mobilePhone, setMobilePhone] = useState<string | null>(null);
  const [waEnabled, setWaEnabled] = useState<boolean>(true);
  const [mounted, setMounted] = useState(false);
  const [locked, setLocked] = useState(sellerType !== 'private' && !!userId);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!userId) return;
    let canceled = false;
    (async () => {
      const { data } = await supabase
        .from('dealer_profiles')
        .select('business_phone, business_phone_fix, whatsapp_enabled')
        .eq('user_id', userId)
        .maybeSingle();
      if (canceled) return;
      if (data && sellerType !== 'private') {
        const { data: sub } = await supabase.rpc('is_user_subscribed', { _uid: userId });
        if (canceled) return;
        setLocked(sub !== true);
      } else {
        setLocked(false);
      }
      if (data) {
        setFixPhone((data as any).business_phone_fix || null);
        setMobilePhone((data as any).business_phone || null);
        setWaEnabled((data as any).whatsapp_enabled !== false);
      }
    })();
    return () => { canceled = true; };
  }, [userId, sellerType]);

  // Prioritate: numărul de pe anunț (listing_contacts) > profil dealer
  const callNumber = phone || fixPhone || mobilePhone;
  const waBase = phone || mobilePhone;
  const waNumber = toWhatsAppNumber(waBase);
  const showWA = !!waNumber && waEnabled;
  const wazeUrl = buildWazeLink(address);

  if (!mounted) return null;

  return createPortal(
    <div
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-background border-t border-border shadow-[0_-4px_12px_rgba(0,0,0,0.08)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex gap-2 px-3 py-3">
        {!locked && callNumber && (
          <a
            href={`tel:${callNumber}`}
            data-seller-contact onClick={() => listingId && trackContactClick({ id: listingId, sellerId: userId, sellerType }, 'phone', 'mobile_bar')}
            className="flex-1 inline-flex items-center justify-center gap-1.5 h-12 rounded bg-primary text-primary-foreground font-semibold text-sm"
          >
            <Phone className="h-5 w-5" />
            Sună vânzătorul
          </a>
        )}
        {!locked && showWA && (
          <a
            href={`https://wa.me/${waNumber}`}
            data-seller-contact onClick={() => listingId && trackContactClick({ id: listingId, sellerId: userId, sellerType }, 'whatsapp', 'mobile_bar')}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1 h-12 px-2 rounded border border-border bg-background text-foreground font-medium text-xs"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 2.1.55 4.15 1.6 5.96L2 22l4.25-1.11a9.93 9.93 0 0 0 5.79 1.84h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.91-7.02A9.86 9.86 0 0 0 12.04 2zm5.79 14.13c-.25.7-1.43 1.33-2 1.41-.51.07-1.15.1-1.86-.12-.43-.13-.98-.31-1.69-.62-2.97-1.28-4.91-4.27-5.06-4.47-.15-.2-1.21-1.61-1.21-3.07s.77-2.18 1.04-2.48c.27-.3.59-.37.79-.37l.57.01c.18.01.43-.07.67.51.25.59.84 2.05.91 2.2.07.15.12.32.02.52-.1.2-.15.32-.3.49-.15.17-.31.39-.45.52-.15.15-.3.31-.13.6.17.3.78 1.28 1.67 2.07 1.15 1.02 2.12 1.34 2.42 1.49.3.15.47.13.65-.08.18-.2.74-.86.94-1.16.2-.3.4-.25.67-.15.27.1 1.72.81 2.02.96.3.15.5.22.57.35.07.13.07.74-.18 1.44z"/></svg>
            WhatsApp
          </a>
        )}
        {wazeUrl && (
          <a
            href={wazeUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Navighează cu Waze"
            className="inline-flex items-center justify-center h-12 px-2 rounded border border-border bg-background text-foreground text-sm"
          >
            <Navigation className="h-5 w-5" />
          </a>
        )}
        <button
          data-seller-contact
          onClick={() => {
            if (listingId) trackListingMessageClick({ id: listingId, sellerId: userId, sellerType });
            (locked && onRequestClick ? onRequestClick : onMessageClick)();
          }}
          className="inline-flex items-center justify-center gap-1 h-12 px-2 rounded border border-border text-foreground font-medium text-xs bg-background"
        >
          <MessageSquare className="h-5 w-5" />
          {locked ? 'Trimite cerere' : 'Mesaj'}
        </button>
      </div>
    </div>,
    document.body
  );
};
